// The camera intake standard (tp-ops create-camera-intake, 2026-09-21).
//
// One pipeline for every photo, from every door, on every device: check the
// pick, cancel the slot's last attempt, sniff for JPEG bytes wearing a .heic
// name, decode through an <img> so the browser applies the EXIF orientation,
// redraw to a canvas so the stored bytes are upright with no EXIF left,
// resize the long edge, upload straight to Filestack with live progress, and
// persist the output=format:jpg transform URL.
//
// The native <input type="file"> is the door on laptops too. The Filestack
// picker uploads raw bytes and skips both the orientation fix and the resize,
// so it is kept for the webcam source alone. No `capture` attribute: without
// it, iOS offers Take Photo and Photo Library together.
//
// The module never knows the form. It writes the URL into the slot's hidden
// input and fires photo-intake:changed on the root; the page counts, gates
// and saves.

import uploadContract from '../../assets/javascripts/photo_upload_contract';
var CDN_HOST = 'https://cdn.filestackcontent.com';
var IMAGE_EXT = /\.(jpe?g|png|webp|heic|heif)$/i;
var HEIC_NAME = /\.(heic|heif)$/i;
var HEIC_MIME = /^image\/hei[cf]/i;
var MAX_BYTES = uploadContract.maxBytes;
var HEIC_LOADER = '/teen-adult-vendor/heic-to-1.5.2/loader.js';
var MISSING_FILE = "We didn't get that photo. Live Photos and iCloud-only photos sometimes don't upload. Tap the slot to try again, or pick a different photo.";
var WRONG_KIND = uploadContract.wrongKind;
var TOO_BIG = uploadContract.tooBig;
var UPLOAD_FAILED = uploadContract.failed;
var NO_CLIENT = 'Photo uploads are not set up on this page.';
var SLOW_8 = 'Large photos can take up to a minute on a slow connection. Leave this page open.';
var SLOW_30 = 'Large photos can take up to a minute on a slow connection. Taking too long? Try a smaller photo.';

var converterLoad = null;
var pageGuarded = false;

function track(type, slot, payload) {
  if (typeof window.avTrackEvent !== 'function') return;
  var body = { slot: slot, via: 'photo_intake' };
  if (payload) {
    Object.keys(payload).forEach(function (key) { body[key] = payload[key]; });
  }
  try { window.avTrackEvent(type, body); } catch (err) { /* telemetry never breaks a capture */ }
}

function cdnUrl(handle) {
  return uploadContract.jpgUrl({ handle: handle });
}

function asFile(blob, name) {
  try {
    return new File([blob], name, { type: blob.type || 'image/jpeg' });
  } catch (err) {
    try { blob.name = name; } catch (ignored) { /* Safari <14 Blob is frozen */ }
    return blob;
  }
}

function looksHeic(file) {
  return HEIC_NAME.test(file.name || '') || HEIC_MIME.test(file.type || '');
}

// The first 12 bytes tell a real HEIF from the JPEG iOS sometimes hands over
// under an .heic name. Those bytes only need the right type and extension.
function sniff(file) {
  if (!looksHeic(file) || !file.slice) return Promise.resolve(file);
  var head = file.slice(0, 12);
  if (!head.arrayBuffer) return Promise.resolve(file);
  return head.arrayBuffer().then(function (buffer) {
    var bytes = new Uint8Array(buffer);
    if (bytes[0] === 255 && bytes[1] === 216 && bytes[2] === 255) {
      return asFile(new Blob([file], { type: 'image/jpeg' }), renameTo(file, '.jpg'));
    }
    if (bytes[0] === 137 && bytes[1] === 80 && bytes[2] === 78 && bytes[3] === 71) {
      return asFile(new Blob([file], { type: 'image/png' }), renameTo(file, '.png'));
    }
    return file;
  }).catch(function () { return file; });
}

function renameTo(file, extension) {
  return String(file.name || 'photo').replace(/\.[^.]*$/, '') + extension;
}

function decode(file) {
  return new Promise(function (resolve, reject) {
    if (!window.URL || !window.URL.createObjectURL) { reject(new Error('object_url_unsupported')); return; }
    var objectUrl = window.URL.createObjectURL(file);
    var img = new window.Image();
    var release = function () {
      try { window.URL.revokeObjectURL(objectUrl); } catch (err) { /* already gone */ }
    };
    img.onload = function () { resolve({ img: img, release: release }); };
    img.onerror = function () { release(); reject(new Error('decode_failed')); };
    img.src = objectUrl;
  });
}

function redraw(slot, file) {
  return decode(file).then(function (loaded) {
    var img = loaded.img;
    var width = img.naturalWidth || img.width;
    var height = img.naturalHeight || img.height;
    if (!width || !height) { loaded.release(); throw new Error('decode_empty'); }

    var scale = Math.min(1, slot.config.maxDim / Math.max(width, height));
    if (scale === 1) {
      track('photo_resize_skipped', slot.name, { reason: 'already_small', width: width, height: height });
    }
    var canvas = document.createElement('canvas');
    if (!canvas.getContext || !canvas.toBlob) { loaded.release(); throw new Error('canvas_unsupported'); }
    canvas.width = Math.max(1, Math.round(width * scale));
    canvas.height = Math.max(1, Math.round(height * scale));
    canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
    loaded.release();

    return new Promise(function (resolve, reject) {
      canvas.toBlob(function (blob) {
        if (!blob) { reject(new Error('encode_failed')); return; }
        resolve(asFile(blob, slot.name + '.jpg'));
      }, 'image/jpeg', slot.config.quality);
    });
  });
}

// The vendored converter is not on every app. Ask the page first, then ask
// the server whether the file is there at all; a 404 means this device
// uploads the raw HEIC and the CDN transform renders it.
function heicConverter() {
  if (window.TeenAdultHeicConverter) return Promise.resolve(window.TeenAdultHeicConverter);
  if (typeof window.avLoadHeicConverter === 'function') return Promise.resolve(window.avLoadHeicConverter());
  if (converterLoad) return converterLoad;
  if (typeof window.fetch !== 'function') return Promise.reject(new Error('converter_absent'));

  converterLoad = window.fetch(HEIC_LOADER, { method: 'HEAD' }).then(function (response) {
    if (!response || !response.ok) throw new Error('converter_absent');
    return loadConverterScript();
  }).catch(function (err) {
    converterLoad = null;
    throw err;
  });
  return converterLoad;
}

function loadConverterScript() {
  return new Promise(function (resolve, reject) {
    var script = document.createElement('script');
    script.type = 'module';
    script.src = HEIC_LOADER;
    var timer = window.setTimeout(function () { finish(new Error('converter_timeout')); }, 20000);
    function finish(err) {
      window.clearTimeout(timer);
      if (!err && window.TeenAdultHeicConverter) { resolve(window.TeenAdultHeicConverter); return; }
      if (script.parentNode) script.parentNode.removeChild(script);
      reject(err || new Error('converter_absent'));
    }
    script.onerror = function () { finish(new Error('converter_failed')); };
    script.onload = function () { finish(null); };
    document.head.appendChild(script);
  });
}

function convertHeic(file) {
  return heicConverter().then(function (convert) {
    return convert({ blob: file, type: 'image/jpeg', quality: 0.92 });
  }).then(function (blob) {
    if (!blob) throw new Error('converter_empty');
    return asFile(blob, renameTo(file, '.jpg'));
  });
}

function prepare(slot, file) {
  setState(slot, 'preparing', 'Preparing the photo…');
  return sniff(file).then(function (candidate) {
    return redraw(slot, candidate).catch(function (err) {
      track('photo_decode_skipped', slot.name, { reason: String(err && (err.message || err)).slice(0, 120) });
      return convertHeic(candidate)
        .then(function (converted) {
          return redraw(slot, converted).catch(function () { return converted; });
        })
        .catch(function (convertErr) {
          track('photo_convert_skipped', slot.name, { reason: String(convertErr && (convertErr.message || convertErr)).slice(0, 120) });
          return candidate;
        });
    });
  });
}

function clientFor(config) {
  if (!config.key) return null;
  if (!window.filestack || typeof window.filestack.init !== 'function') return null;
  try { return window.filestack.init(config.key); } catch (err) { return null; }
}

function setState(slot, state, text) {
  slot.el.setAttribute('data-state', state);
  slot.el.classList.toggle('is-failed', state === 'failed');
  slot.el.classList.toggle('is-busy', state === 'selecting' || state === 'preparing' || state === 'uploading' || state === 'processing');
  if (text !== undefined && slot.status) slot.status.textContent = text;
}

function fail(slot, message) {
  setState(slot, 'failed', message);
  if (!slot.status) return;
  var retry = document.createElement('button');
  retry.type = 'button';
  retry.className = 'photo-intake__retry';
  retry.setAttribute('data-role', 'retry');
  retry.textContent = 'Retry';
  retry.addEventListener('click', function () {
    if (slot.lastFile) { takeFile(slot, slot.lastFile); return; }
    slot.file.click();
  });
  slot.status.appendChild(document.createTextNode(' '));
  slot.status.appendChild(retry);
}

function watchStart(slot) {
  slot.config.watch[slot.name] = Date.now();
  if (!slot.config.timer) {
    slot.config.timer = window.setInterval(function () { watchTick(slot.config); }, 1000);
  }
  watchTick(slot.config);
}

function watchStop(slot) {
  delete slot.config.watch[slot.name];
  if (Object.keys(slot.config.watch).length > 0) { watchTick(slot.config); return; }
  if (slot.config.timer) { window.clearInterval(slot.config.timer); slot.config.timer = null; }
  if (slot.config.slow) { slot.config.slow.textContent = ''; slot.config.slow.classList.add('is-hide'); }
}

function watchTick(config) {
  var box = config.slow;
  if (!box) return;
  var starts = Object.keys(config.watch).map(function (key) { return config.watch[key]; });
  if (starts.length === 0) return;
  var elapsed = Math.floor((Date.now() - Math.min.apply(null, starts)) / 1000);
  if (elapsed < 8) { box.textContent = ''; box.classList.add('is-hide'); return; }
  box.textContent = elapsed < 30 ? SLOW_8 : SLOW_30;
  box.classList.remove('is-hide');
}

function cancelInFlight(slot) {
  if (slot.token && typeof slot.token.cancel === 'function') {
    try { slot.token.cancel(); } catch (err) { /* already settled */ }
  }
  slot.token = null;
  watchStop(slot);
}

function upload(slot, attempt, blob) {
  var client = clientFor(slot.config);
  if (!client) {
    track('filestack_init_failed', slot.name, {});
    fail(slot, NO_CLIENT);
    return null;
  }

  var startedAt = Date.now();
  var progressed = false;
  var flagged = false;
  watchStart(slot);
  setState(slot, 'uploading', 'Uploading…');

  var heartbeat = window.setInterval(function () {
    if (attempt !== slot.attempt) { window.clearInterval(heartbeat); return; }
    if (progressed) return;
    var elapsed = Math.floor((Date.now() - startedAt) / 1000);
    slot.status.textContent = (elapsed >= 5 ? 'Still uploading… ' : 'Uploading… ') + elapsed + 's';
    if (!flagged && elapsed >= 5) {
      flagged = true;
      track('upload_no_progress', slot.name, { elapsed_seconds: elapsed });
    }
  }, 1000);

  var token = {};
  slot.token = token;

  return client.upload(blob, {
    onProgress: function (event) {
      progressed = true;
      if (attempt !== slot.attempt) return;
      if (!event || typeof event.totalPercent !== 'number') return;
      var pct = Math.max(0, Math.min(100, Math.round(event.totalPercent)));
      if (pct >= 99) { setState(slot, 'processing', 'Processing…'); return; }
      setState(slot, 'uploading', 'Uploading ' + pct + '%');
    }
  }, { filename: blob.name || (slot.name + '.jpg') }, token).then(function (result) {
    window.clearInterval(heartbeat);
    watchStop(slot);
    if (attempt !== slot.attempt) return;
    slot.token = null;
    track('upload_finished', slot.name, {
      handle: result && result.handle,
      duration_ms: Date.now() - startedAt,
      progress_fired: progressed
    });
    persist(slot, result && result.handle ? cdnUrl(result.handle) : (result && result.url));
  }).catch(function (err) {
    window.clearInterval(heartbeat);
    watchStop(slot);
    if (attempt !== slot.attempt) return;
    slot.token = null;
    throw err;
  });
}

function persist(slot, url) {
  if (!url) {
    fail(slot, UPLOAD_FAILED);
    return;
  }
  slot.hidden.value = url;
  var img = slot.el.querySelector('[data-role="img"]');
  if (!img) {
    img = document.createElement('img');
    img.className = 'photo-intake__img';
    img.setAttribute('data-role', 'img');
    slot.tile.insertBefore(img, slot.tile.firstChild);
  }
  img.src = url;
  img.alt = slot.el.getAttribute('data-label') || slot.name;
  slot.el.classList.add('is-added');
  setState(slot, 'added', 'Added');
  announce(slot, url);
}

function announce(slot, url) {
  var detail = { slot: slot.name, url: url };
  var event;
  try {
    event = new CustomEvent('photo-intake:changed', { bubbles: true, detail: detail });
  } catch (err) {
    event = document.createEvent('CustomEvent');
    event.initCustomEvent('photo-intake:changed', true, false, detail);
  }
  slot.root.dispatchEvent(event);
}

function clearSlot(slot) {
  cancelInFlight(slot);
  slot.attempt += 1;
  slot.lastFile = null;
  slot.hidden.value = '';
  slot.file.value = '';
  var img = slot.el.querySelector('[data-role="img"]');
  if (img && img.parentNode) img.parentNode.removeChild(img);
  slot.el.classList.remove('is-added');
  setState(slot, 'empty', '');
  announce(slot, '');
}

function takeFile(slot, file) {
  cancelInFlight(slot);
  var attempt = ++slot.attempt;
  slot.file.value = '';

  if (!file || file.size === 0) {
    slot.lastFile = null;
    track('file_missing', slot.name, { had_file: !!file, size: file ? file.size : null });
    fail(slot, MISSING_FILE);
    return;
  }

  var type = String(file.type || '').toLowerCase();
  var name = String(file.name || '').toLowerCase();
  var isImage = type ? type.indexOf('image/') === 0 : IMAGE_EXT.test(name);
  if (!isImage) {
    slot.lastFile = null;
    track('file_wrong_kind', slot.name, { mimetype: type, filename: name.slice(-40) });
    fail(slot, WRONG_KIND);
    return;
  }
  if (file.size > MAX_BYTES) {
    slot.lastFile = null;
    track('file_too_large', slot.name, { size: file.size });
    fail(slot, TOO_BIG);
    return;
  }

  slot.lastFile = file;
  setState(slot, 'selecting', 'Checking that photo…');

  prepare(slot, file).then(function (blob) {
    if (attempt !== slot.attempt) return null;
    return upload(slot, attempt, blob);
  }).catch(function (err) {
    if (attempt !== slot.attempt) return;
    track('upload_failed', slot.name, { error: String(err && (err.message || err)).slice(0, 300) });
    fail(slot, UPLOAD_FAILED);
  });
}

function openWebcam(slot) {
  var client = clientFor(slot.config);
  if (!client) {
    track('filestack_init_failed', slot.name, { door: 'webcam' });
    fail(slot, NO_CLIENT);
    return;
  }
  slot.lastFile = null;
  var attempt = ++slot.attempt;
  setState(slot, 'selecting', 'Opening the webcam…');
  client.picker({
    accept: uploadContract.imageTypes,
    maxSize: uploadContract.maxBytes,
    fromSources: ['webcam'],
    maxFiles: 1,
    uploadInBackground: false,
    onFileUploadFinished: function (result) {
      if (attempt !== slot.attempt) return;
      persist(slot, result && result.handle ? cdnUrl(result.handle) : (result && result.url));
    },
    onFileUploadFailed: function (file, err) {
      if (attempt !== slot.attempt) return;
      track('upload_failed', slot.name, { door: 'webcam', error: String(err && (err.message || err)).slice(0, 300) });
      fail(slot, UPLOAD_FAILED);
    },
    onCancel: function () {
      if (attempt !== slot.attempt) return;
      setState(slot, slot.el.classList.contains('is-added') ? 'added' : 'empty', '');
    }
  }).open();
}

function guardPageDrops() {
  if (pageGuarded) return;
  pageGuarded = true;
  ['dragover', 'drop'].forEach(function (name) {
    document.addEventListener(name, function (event) {
      var types = event.dataTransfer && event.dataTransfer.types;
      if (types && Array.prototype.indexOf.call(types, 'Files') !== -1) event.preventDefault();
    });
  });
}

function bindSlot(root, config, el) {
  var slot = {
    root: root,
    config: config,
    el: el,
    name: el.getAttribute('data-slot'),
    hidden: el.querySelector('[data-role="url"]'),
    tile: el.querySelector('[data-role="tile"]'),
    file: el.querySelector('[data-role="file"]'),
    status: el.querySelector('[data-role="status"]'),
    attempt: 0,
    depth: 0,
    token: null,
    lastFile: null
  };
  if (!slot.hidden || !slot.file || !slot.tile) return;

  slot.file.addEventListener('change', function () {
    takeFile(slot, slot.file.files && slot.file.files[0]);
  });
  slot.tile.addEventListener('click', function (event) {
    event.preventDefault();
    slot.file.click();
  });

  var replace = el.querySelector('[data-role="replace"]');
  if (replace) {
    replace.addEventListener('click', function (event) {
      event.preventDefault();
      slot.file.click();
    });
  }

  var remove = el.querySelector('[data-role="remove"]');
  if (remove) {
    remove.addEventListener('click', function (event) {
      event.preventDefault();
      clearSlot(slot);
    });
  }

  var webcam = el.querySelector('[data-role="webcam"]');
  if (webcam) {
    webcam.addEventListener('click', function (event) {
      event.preventDefault();
      openWebcam(slot);
    });
  }

  el.addEventListener('dragenter', function (event) {
    event.preventDefault();
    slot.depth += 1;
    el.classList.add('is-dropping');
  });
  el.addEventListener('dragover', function (event) { event.preventDefault(); });
  el.addEventListener('dragleave', function () {
    slot.depth -= 1;
    if (slot.depth > 0) return;
    slot.depth = 0;
    el.classList.remove('is-dropping');
  });
  el.addEventListener('drop', function (event) {
    event.preventDefault();
    event.stopPropagation();
    slot.depth = 0;
    el.classList.remove('is-dropping');
    var files = event.dataTransfer && event.dataTransfer.files;
    if (!files || files.length === 0) return;
    if (files.length > 1) { fail(slot, 'Drop one photo at a time.'); return; }
    takeFile(slot, files[0]);
  });
}

function followViewport(root, config) {
  var query = window.matchMedia ? window.matchMedia('(max-width: ' + config.mobileMax + 'px)') : null;
  var apply = function () {
    var mobile = query ? query.matches : false;
    root.classList.toggle('is-mobile', mobile);
    root.classList.toggle('is-desktop', !mobile);
  };
  apply();
  if (!query) return;
  if (query.addEventListener) query.addEventListener('change', apply);
  else if (query.addListener) query.addListener(apply);
}

function bindRoot(root) {
  if (root.getAttribute('data-photo-intake-bound') === 'true') return;
  root.setAttribute('data-photo-intake-bound', 'true');

  var config = {
    key: root.getAttribute('data-filestack-key') || '',
    maxDim: parseInt(root.getAttribute('data-max-dim'), 10) || 1600,
    quality: parseFloat(root.getAttribute('data-quality')) || 0.85,
    mobileMax: parseFloat(root.getAttribute('data-mobile-max')) || 767.98,
    slow: root.querySelector('[data-role="slow"]'),
    watch: {},
    timer: null
  };

  followViewport(root, config);
  Array.prototype.forEach.call(root.querySelectorAll('[data-slot]'), function (el) {
    bindSlot(root, config, el);
  });
}

function bind(scope) {
  var within = scope || document;
  if (within.nodeType === 1 && within.hasAttribute('data-photo-intake')) bindRoot(within);
  Array.prototype.forEach.call(within.querySelectorAll('[data-photo-intake]'), bindRoot);
  guardPageDrops();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function () { bind(); });
} else {
  bind();
}
document.addEventListener('turbolinks:load', function () { bind(); });

window.PhotoIntake = { bind: bind };

export { bind };
